// Compact, Stripe-style "Pages" dropdown that replaces the legacy mega-menu.
import type { FC, MouseEvent, ReactNode } from 'react'

import { Fragment, useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { gsap, registerGsap } from '@/lib/gsap'
import { pagesMenu, pagesUtility } from '@/constants/navigation'

const DropLink: FC<{
    href: string
    external?: boolean
    className?: string
    children: ReactNode
    onClick?: () => void
    onMouseEnter?: (event: MouseEvent<HTMLElement>) => void
}> = ({ href, external, className, children, onClick, onMouseEnter }) => {
    const isRoute = href.startsWith('/') && !href.startsWith('/#')

    if (isRoute && !external) {
        return (
            <Link href={href} className={className} onClick={onClick} onMouseEnter={onMouseEnter}>
                {children}
            </Link>
        )
    }

    return (
        <a
            href={href}
            className={className}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
            {children}
        </a>
    )
}

interface PagesDropdownProps {
    onNavigate?: () => void
}

/**
 * Compact, Stripe-style "Pages" dropdown. The panel touches the toggle (a
 * transparent pad provides the visual offset) so the hover area is continuous
 * and the menu never closes on the way in. GSAP drives the card's open reveal
 * (scale/fade + staggered items) and a highlight that glides between rows.
 */
export const PagesDropdown: FC<PagesDropdownProps> = ({ onNavigate }) => {
    const [open, setOpen] = useState(false)
    const panelRef = useRef<HTMLDivElement | null>(null)
    const cardRef = useRef<HTMLDivElement | null>(null)
    const listRef = useRef<HTMLDivElement | null>(null)
    const highlightRef = useRef<HTMLDivElement | null>(null)
    const closeTimer = useRef<number | null>(null)

    useEffect(() => {
        registerGsap()
        const panel = panelRef.current
        const card = cardRef.current
        if (!panel || !card) return

        const items = listRef.current
            ? (Array.from(listRef.current.querySelectorAll('.nav-pages-item')) as HTMLElement[])
            : []

        gsap.killTweensOf(card)
        gsap.killTweensOf(items)

        if (open) {
            gsap.set(panel, { display: 'block' })
            gsap
                .timeline()
                .fromTo(
                    card,
                    { opacity: 0, y: -12, scale: 0.96 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.42, ease: 'expo.out' }
                )
                .fromTo(
                    items,
                    { opacity: 0, y: 10 },
                    { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: 'power3.out' },
                    '-=0.28'
                )
        } else {
            gsap.to(card, {
                opacity: 0,
                y: -8,
                scale: 0.98,
                duration: 0.2,
                ease: 'power2.in',
                onComplete: () => {
                    gsap.set(panel, { display: 'none' })
                    if (highlightRef.current) gsap.set(highlightRef.current, { opacity: 0 })
                }
            })
        }
    }, [open])

    useEffect(
        () => () => {
            if (closeTimer.current) window.clearTimeout(closeTimer.current)
        },
        []
    )

    const cancelClose = () => {
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    const scheduleClose = () => {
        cancelClose()
        closeTimer.current = window.setTimeout(() => setOpen(false), 220)
    }

    const openNow = () => {
        cancelClose()
        setOpen(true)
    }

    const moveHighlight = (event: MouseEvent<HTMLElement>) => {
        const highlight = highlightRef.current
        const el = event.currentTarget
        if (!highlight) return
        gsap.to(highlight, {
            y: el.offsetTop,
            height: el.offsetHeight,
            opacity: 1,
            duration: 0.35,
            ease: 'power3.out'
        })
    }

    const hideHighlight = () => {
        if (highlightRef.current) gsap.to(highlightRef.current, { opacity: 0, duration: 0.25 })
    }

    const handleNavigate = () => {
        setOpen(false)
        onNavigate?.()
    }

    return (
        <div className='nav-link-holder'>
            <div
                className={cn('nav-dropdown nav-pages w-dropdown', open && 'is-open')}
                style={{ position: 'relative' }}
                onMouseEnter={openNow}
                onMouseLeave={scheduleClose}
            >
                <div
                    className='nav-link nav-link-dropdown w-dropdown-toggle'
                    role='button'
                    tabIndex={0}
                    aria-haspopup='menu'
                    aria-expanded={open}
                    onClick={() => setOpen((prev) => !prev)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            setOpen((prev) => !prev)
                        }
                    }}
                >
                    <div className='nav-dropdown-icon w-icon-dropdown-toggle' />
                    <div>Pages</div>
                    <div className='link-background' />
                    <div className='nav-dropdown-link-arrow-holder on-rounded-nav'>
                        <div className='nav-dropdown-link-arrow-animated'>
                            <div className='nav-dropdown-link-arrow' />
                        </div>
                    </div>
                </div>

                <div className='nav-pages-panel' ref={panelRef} role='menu'>
                    <div className='nav-pages-card' ref={cardRef}>
                        <div className='nav-pages-list' ref={listRef} onMouseLeave={hideHighlight}>
                            <div className='nav-pages-highlight' ref={highlightRef} />
                            {pagesMenu.map((item) => (
                                <DropLink
                                    key={item.label}
                                    href={item.href}
                                    external={item.external}
                                    className='nav-pages-item'
                                    onClick={handleNavigate}
                                    onMouseEnter={moveHighlight}
                                >
                                    <span className='nav-pages-title'>{item.label}</span>
                                    <span className='nav-pages-desc'>{item.description}</span>
                                </DropLink>
                            ))}
                        </div>

                        <div className='nav-pages-footer'>
                            <span className='nav-pages-footer-label'>More</span>
                            {pagesUtility.map((link, index) => (
                                <Fragment key={link.label}>
                                    {index > 0 && <span className='nav-pages-foot-sep'>·</span>}
                                    <DropLink
                                        href={link.href}
                                        external={link.external}
                                        className='nav-pages-foot-link'
                                        onClick={handleNavigate}
                                    >
                                        {link.label}
                                    </DropLink>
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
